<template>
  <div class="transaction-volume-widget">
    <div v-if="loading" class="text-center py-6">
      <v-progress-circular indeterminate size="32" color="primary" />
      <div class="text-caption grey--text mt-2">
        Loading transaction data...
      </div>
    </div>
    <div v-else-if="fetchError" class="text-center py-4">
      <v-icon color="grey lighten-1" size="32">mdi-alert-circle-outline</v-icon>
      <p class="text-caption grey--text mt-2">Unable to load data</p>
    </div>
    <div v-else>
      <!-- Stats row as pill badges -->
      <div class="stats-row d-flex flex-wrap mb-4">
        <div class="stat-pill mr-2 mb-2">
          <span class="stat-pill-label">Collected (YTD)</span>
          <span class="stat-pill-value" :class="toneText('green')">
            {{ formatCurrency(totalAmount) }}
          </span>
        </div>
        <div class="stat-pill mr-2 mb-2">
          <span class="stat-pill-label">vs Last Year</span>
          <span
            class="stat-pill-value"
            :class="toneText(yoyChange >= 0 ? 'green' : 'red')"
          >
            {{ yoyChange >= 0 ? '+' : '' }}{{ yoyChange }}%
          </span>
        </div>
        <div class="stat-pill mr-2 mb-2">
          <span class="stat-pill-label">Peak</span>
          <span class="stat-pill-value" :class="toneText('orange')">
            {{ peakLabel }}
          </span>
        </div>
        <div class="stat-pill mr-2 mb-2">
          <span class="stat-pill-label">Payments</span>
          <span class="stat-pill-value" :class="toneText('blue')">{{ totalPayments }}</span>
        </div>
      </div>

      <!-- Controls row -->
      <div class="d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center">
          <span class="legend-swatch" :style="{ background: seriesColors[0] }"></span>
          <span class="text-caption mr-3">This Year</span>
          <span class="legend-swatch" :style="{ background: priorSwatchGradient }"></span>
          <span class="text-caption">Last Year</span>
        </div>
        <v-btn-toggle
          v-model="granularity"
          dense
          mandatory
          borderless
          class="range-toggle"
          @change="fetchData"
        >
          <v-btn x-small text value="month">Month</v-btn>
          <v-btn x-small text value="tenday">10-day</v-btn>
        </v-btn-toggle>
      </div>

      <!-- ApexCharts area chart. :key forces a clean remount on granularity
           change so vue-apexcharts doesn't accumulate duplicate series. The theme is in the key
           for the same reason: ApexCharts applies `theme.mode` when it builds the chart, and an
           in-place updateOptions leaves some of the old palette behind. -->
      <apexchart
        :key="`${granularity}-${isDark ? 'dark' : 'light'}`"
        type="area"
        height="260"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { clients } from '../../../util/clients';
import WidgetThemeMixin from '../WidgetTheme.mixin';

const { backendRest } = clients.direct;

/* Line colours, not fills: on #1e1e1e the 800-weight blue of the light theme sinks into the card,
   so each series is lifted to the shade that still reads as the same hue. The legend swatches are
   drawn from this same pair rather than repeating the hexes, so they cannot fall out of step with
   the chart they label. */
const SERIES_COLORS = {
  light: ['#1565c0', '#ef5350'],
  dark: ['#42a5f5', '#ef9a9a'],
};

export default {
  name: 'TransactionVolumeWidget',
  components: {
    apexchart: VueApexCharts,
  },
  mixins: [WidgetThemeMixin],
  data: () => ({
    granularity: 'month',
    fiscalYear: null,
    buckets: [],
    loading: true,
    fetchError: false,
  }),
  computed: {
    seriesColors() {
      return this.isDark ? SERIES_COLORS.dark : SERIES_COLORS.light;
    },
    // The "Last Year" line is dashed in the chart; the swatch repeats that as a dashed bar.
    priorSwatchGradient() {
      const c = this.seriesColors[1];
      return `repeating-linear-gradient(to right, ${c} 0px, ${c} 4px, transparent 4px, transparent 8px)`;
    },
    // Buckets the current fiscal year has actually reached (data so far).
    elapsed() {
      return this.buckets.filter((b) => b.current);
    },
    xCategories() {
      return this.buckets.map((b) => b.label);
    },
    chartSeries() {
      return [
        {
          name: 'This Year',
          data: this.buckets.map((b) => (b.current ? b.current.amount : null)),
        },
        {
          name: 'Last Year',
          data: this.buckets.map((b) => b.prior.amount),
        },
      ];
    },
    totalAmount() {
      return this.elapsed.reduce((sum, b) => sum + b.current.amount, 0);
    },
    totalPayments() {
      return this.elapsed.reduce((sum, b) => sum + b.current.count, 0);
    },
    // Same periods last year, so the YoY % is YTD-vs-YTD (like-for-like).
    priorSamePeriod() {
      return this.elapsed.reduce((sum, b) => sum + b.prior.amount, 0);
    },
    yoyChange() {
      if (this.priorSamePeriod === 0) return 0;
      return Math.round(
        ((this.totalAmount - this.priorSamePeriod) / this.priorSamePeriod) * 100,
      );
    },
    peakLabel() {
      if (this.elapsed.length === 0) return '-';
      const peak = this.elapsed.reduce(
        (max, b) => (b.current.amount > max.current.amount ? b : max),
        this.elapsed[0],
      );
      return peak.label;
    },
    chartOptions() {
      const axisText = this.isDark ? 'rgba(255, 255, 255, 0.6)' : '#9e9e9e';
      const gridColor = this.isDark ? 'rgba(255, 255, 255, 0.10)' : '#f0f0f0';
      return {
        chart: {
          type: 'area',
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: true, speed: 600 },
          fontFamily: 'Roboto, sans-serif',
          // ApexCharts paints its own white canvas unless told not to; left on, the chart is a
          // white rectangle inside the dark card. `theme.mode` only recolours text and tooltip.
          background: 'transparent',
        },
        theme: { mode: this.isDark ? 'dark' : 'light' },
        colors: this.seriesColors,
        stroke: {
          curve: 'smooth',
          width: [2, 2],
          dashArray: [0, 5],
        },
        fill: {
          type: ['gradient', 'solid'],
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.45,
            opacityTo: 0.02,
            stops: [0, 90, 100],
          },
          opacity: [1, 0],
        },
        xaxis: {
          categories: this.xCategories,
          labels: {
            style: { fontSize: '11px', colors: axisText },
            rotate: -30,
            rotateAlways: false,
            trim: false,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tickAmount: 12,
        },
        yaxis: {
          labels: {
            style: { fontSize: '11px', colors: axisText },
            formatter: (val) => this.formatCurrency(val),
          },
          min: 0,
        },
        grid: {
          borderColor: gridColor,
          strokeDashArray: 4,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
          padding: { left: 4, right: 4 },
        },
        dataLabels: { enabled: false },
        markers: { size: 0 },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val) => (val == null ? 'n/a' : this.formatCurrency(val)),
          },
        },
        legend: { show: false },
      };
    },
  },
  async created() {
    await this.fetchData();
  },
  methods: {
    formatCurrency(val) {
      if (!val) return '$0';
      if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
      if (val >= 1000) return `$${(val / 1000).toFixed(1)}K`;
      return `$${val.toFixed(0)}`;
    },
    async fetchData() {
      this.loading = true;
      try {
        const resp = await backendRest.get('/dashboard/transaction-volume', {
          params: { granularity: this.granularity },
        });
        this.buckets = resp.data.buckets || [];
        this.fiscalYear = resp.data.fiscalYear || null;
        this.fetchError = false;
      } catch (e) {
        console.error('TransactionVolume fetch error', e);
        this.fetchError = true;
        this.buckets = [];
      } finally {
        this.loading = false;
      }
    },
    async refresh() {
      await this.fetchData();
    },
  },
};
</script>

<style scoped>
.stats-row {
  gap: 0;
}

.stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--widget-pill-bg, #f0f4ff);
  border-radius: 20px;
  padding: 5px 14px;
  border: 1px solid var(--widget-pill-border, #e0e8ff);
}

.stat-pill-label {
  font-size: 11px;
  color: var(--widget-muted-text, #757575);
  font-weight: 500;
  white-space: nowrap;
}

.stat-pill-value {
  font-size: 14px;
  font-weight: 800;
  white-space: nowrap;
}

/* Both swatch fills are bound from seriesColors so they track the chart across themes. */
.legend-swatch {
  width: 24px;
  height: 3px;
  border-radius: 2px;
  display: inline-block;
  margin-right: 4px;
}

.range-toggle {
  background: transparent !important;
}

.range-toggle .v-btn {
  font-size: 11px !important;
  min-width: 32px !important;
  padding: 0 6px !important;
}

.range-toggle .v-btn--active {
  background: var(--widget-toggle-active-bg, #e3f2fd) !important;
  color: var(--widget-toggle-active-text, #1565c0) !important;
}
</style>
